import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, HelpCircle, CheckCircle, Heart, Sparkles, ShieldAlert } from 'lucide-react';

export default function AdoptionGuidePage() {
  const steps = [
    { stage: 'Stage 1', action: 'Browse Available Friends', desc: 'Explore our certified adoptable companion feed. Profiles include veterinary clearance logs, breed heritage, and age.' },
    { stage: 'Stage 2', action: 'Veterinary Call Review', desc: 'Consult with our certified veterinary staff. We will detail the pet\'s history, complete vaccination logs, and diet preferences.' },
    { stage: 'Stage 3', action: 'Safe Climate Transit', desc: 'We transport your companion in a temperature-regulated, handler-supervised van directly to your home to ensure zero transit stress.' },
    { stage: 'Stage 4', action: 'Verification & Handover', desc: 'Final documentation checks are completed. You receive the official health logbook, microchip registry card, and a complimentary starter kit.' },
  ];

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 md:py-16 space-y-12">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-xs text-gray-550 mb-4 overflow-x-auto whitespace-nowrap">
        <Link to="/" className="hover:text-primary transition-colors font-medium">Home</Link>
        <ChevronRight size={12} className="text-gray-400" />
        <span className="text-primary font-bold">Adoption Guide</span>
      </nav>

      {/* Hero Header */}
      <div className="text-center space-y-4 max-w-2xl mx-auto">
        <div className="w-12 h-12 bg-white border border-accent/25 rounded-full flex items-center justify-center mx-auto text-accent shadow-sm">
          <Heart size={20} />
        </div>
        <h1 className="text-3xl md:text-4.5xl font-serif font-extrabold text-primary tracking-tight">Pet Adoption Guidelines</h1>
        <div className="w-10 h-0.5 bg-accent/40 mx-auto" />
        <p className="text-gray-500 text-xs md:text-sm font-semibold leading-relaxed">
          Bringing a companion home is an exciting life change. Our structured process guarantees breeder heritage, veterinary clearance, and a stress-free transition.
        </p>
      </div>

      {/* Main Grid Panels */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        {/* Stages Timeline (Left Column) */}
        <div className="lg:col-span-7 bg-white rounded-[32px] border border-gray-100 p-6 md:p-10 shadow-premium space-y-8">
          <div className="border-b border-gray-50 pb-4">
            <h2 className="text-base font-serif font-extrabold text-primary flex items-center gap-2">
              <Sparkles size={16} className="text-accent" /> Steps & Safe Transit Protocols
            </h2>
            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mt-0.5">Timeline to bring your companion home</p>
          </div>
          <div className="space-y-6">
            {steps.map((s, idx) => (
              <div key={idx} className="flex gap-4 border-b border-gray-50 pb-6 last:border-0 last:pb-0">
                <span className="inline-flex w-14 h-14 rounded-2xl bg-[#FCFAF7] border border-accent/10 text-accent font-extrabold text-xs flex items-center justify-center flex-shrink-0 shadow-sm">
                  {s.stage}
                </span>
                <div className="space-y-1">
                  <h3 className="font-extrabold text-primary text-xs uppercase tracking-wider">{s.action}</h3>
                  <p className="text-gray-500 text-xs leading-relaxed font-semibold">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Requirements & Call-out (Right Column) */}
        <div className="lg:col-span-5 space-y-6">
          {/* Checklist Card */}
          <div className="bg-[#FCFAF7] rounded-[32px] p-8 border border-accent/15 space-y-6">
            <h3 className="font-serif font-extrabold text-primary text-sm uppercase tracking-wider flex items-center gap-2">
              📋 Pre-Adoption Checklist
            </h3>
            <ul className="space-y-4 text-xs font-semibold text-gray-650 leading-relaxed">
              <li className="flex gap-3 items-start">
                <CheckCircle size={15} className="text-accent flex-shrink-0 mt-0.5" />
                <div>
                  <strong className="text-primary block font-bold mb-0.5">Home Preparation:</strong>
                  Set up basic supplies (puppy pads, kitten boxes, feeding bowls, scratch posts) in a quiet zone before pet arrival.
                </div>
              </li>
              <li className="flex gap-3 items-start">
                <CheckCircle size={15} className="text-accent flex-shrink-0 mt-0.5" />
                <div>
                  <strong className="text-primary block font-bold mb-0.5">Vet Clearance Checkups:</strong>
                  We recommend taking your companion to your family vet within the first week to review the deworming logbook.
                </div>
              </li>
              <li className="flex gap-3 items-start">
                <CheckCircle size={15} className="text-accent flex-shrink-0 mt-0.5" />
                <div>
                  <strong className="text-primary block font-bold mb-0.5">Acclimation Phase:</strong>
                  Allow 2-3 weeks for companions to adapt to home sounds and routines. Keep schedules steady and stress-free.
                </div>
              </li>
            </ul>
          </div>

          {/* Expert Consult Card */}
          <div className="bg-white border border-gray-100 rounded-[32px] p-6 md:p-8 shadow-premium text-center space-y-4">
            <div className="w-10 h-10 bg-accent/10 rounded-full flex items-center justify-center mx-auto text-accent shadow-sm">
              <HelpCircle size={18} />
            </div>
            <div className="space-y-1">
              <h4 className="font-extrabold text-primary text-xs uppercase tracking-wider">Speak to an Adoption Expert</h4>
              <p className="text-gray-500 text-xs font-medium leading-relaxed max-w-xs mx-auto">
                Have questions regarding vaccine schedules, breed suitability, or custom diets? Call or WhatsApp our care staff directly.
              </p>
            </div>
            <div className="pt-2">
              <a
                href="https://wa.me/923295025172"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full inline-flex items-center justify-center bg-primary hover:bg-accent text-white py-3.5 text-xs font-bold uppercase tracking-widest rounded-xl transition-all shadow-md active:scale-95"
              >
                Consult via WhatsApp
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Security/FAQ section */}
      <div className="bg-[#FCFAF7] rounded-[32px] p-8 md:p-12 border border-accent/10 text-center space-y-6">
        <div className="w-10 h-10 bg-accent/15 text-accent rounded-full flex items-center justify-center mx-auto">
          <ShieldAlert size={18} />
        </div>
        <div className="space-y-2 max-w-md mx-auto">
          <h2 className="text-xl font-serif font-extrabold text-primary">Frequently Asked Questions</h2>
          <p className="text-gray-550 text-xs font-medium leading-relaxed">
            Need details on medical insurance, pedigree breeder certificates, or climate-controlled delivery routes? Check our FAQ library.
          </p>
        </div>
        <div className="pt-2">
          <Link to="/faqs" className="inline-block bg-primary hover:bg-accent text-white px-8 py-3.5 text-xs font-bold uppercase tracking-widest rounded-xl shadow-md transition-all">
            View FAQ Library
          </Link>
        </div>
      </div>
    </div>
  );
}
